import { Column, RichTextDisplay } from "@/components";
import { ReadOnlyMediaGallery } from "@/components/companies/company-intake/general-media/ReadOnlyMediaGallery";
import { InfoTooltip } from "@/components/form/InfoTooltip";
import { Label } from "@/components/ui/label";

import { cn } from "@/lib/utils";
import { MediaLink } from "@/types";

const VALUE_CLASSES = "rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-800";

const ReadOnlyLabel = ({
  children,
  infoTooltip,
}: {
  children: React.ReactNode;
  infoTooltip?: string;
}) => (
  <Label className="mb-1 flex items-center gap-1 border-b border-slate-200 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
    <span className="truncate">{children}</span>
    {infoTooltip && <InfoTooltip className="shrink-0" content={infoTooltip} />}
  </Label>
);

const EmptyValue = () => <div className={VALUE_CLASSES}>{"\u2014"}</div>;

export const ReadOnlyField = ({
  className,
  infoTooltip,
  label,
  mono,
  value,
}: {
  className?: string;
  infoTooltip?: string;
  label: string;
  mono?: boolean;
  value?: string | null;
}) => {
  return (
    <Column className={cn("min-w-0 gap-0.5", className)}>
      <ReadOnlyLabel infoTooltip={infoTooltip}>{label}</ReadOnlyLabel>
      {value ? (
        <div
          className={cn(
            VALUE_CLASSES,
            mono && "font-mono text-xs leading-relaxed",
          )}
        >
          {value}
        </div>
      ) : (
        <EmptyValue />
      )}
    </Column>
  );
};

export const ReadOnlyRichTextField = ({
  className,
  label,
  value,
}: {
  className?: string;
  label?: string;
  value?: string | null;
}) => {
  const isEmpty = !value || value === "<p></p>";

  return (
    <Column className={cn("gap-0.5", className)}>
      {label && <ReadOnlyLabel>{label}</ReadOnlyLabel>}
      {isEmpty ? (
        <EmptyValue />
      ) : (
        <RichTextDisplay className={VALUE_CLASSES} richText={value} />
      )}
    </Column>
  );
};

export const ReadOnlyLinkField = ({
  className,
  label,
  url,
}: {
  className?: string;
  label: string;
  url?: string | null;
}) => {
  return (
    <Column className={cn("min-w-0 gap-0.5", className)}>
      <ReadOnlyLabel>{label}</ReadOnlyLabel>
      {url ? (
        <a
          className="block truncate rounded-md bg-slate-50 px-3 py-2 text-sm text-primary-600 underline"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      ) : (
        <EmptyValue />
      )}
    </Column>
  );
};

export const ReadOnlyGeneralMediaField = ({
  className,
  mediaLinks,
}: {
  className?: string;
  mediaLinks: MediaLink[];
}) => {
  const hasMedia = mediaLinks.length > 0;

  return (
    <Column className={cn("min-w-0 gap-0.5", className)}>
      <ReadOnlyLabel>Media</ReadOnlyLabel>
      {hasMedia ? (
        <ReadOnlyMediaGallery mediaLinks={mediaLinks} />
      ) : (
        <EmptyValue />
      )}
    </Column>
  );
};

export const ReadOnlyBooleanField = ({
  className,
  isSelected,
  label,
}: {
  className?: string;
  isSelected?: boolean | null;
  label: string;
}) => {
  return (
    <Column className={cn("gap-0.5", className)}>
      <ReadOnlyLabel>{label}</ReadOnlyLabel>
      <div className={VALUE_CLASSES}>{isSelected ? "Yes" : "No"}</div>
    </Column>
  );
};
